import React, { useState, useEffect, useCallback } from 'react';
import { Row, Col, Card, Badge, Button, Spinner, Alert, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSync,
    faSearch,
    faTimesCircle,
    faExternalLinkAlt,
    faBarcode
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';

import { db } from 'config/firebase.js';
import { collection, query, orderBy, onSnapshot, where, limit, getDocs } from 'firebase/firestore';
import missingImage from 'assets/merlin/missing_image.png';

const CLOUD_FUNCTIONS_URL =
    process.env.REACT_APP_CLOUD_FUNCTIONS_URL || 'https://us-central1-stk-merlin.cloudfunctions.net';

const VERIFICATIONS_PATH = 'productCatalog/topicz/asinVerifications';
const PRODUCTS_PATH = 'productCatalog/topicz/products';

/**
 * NoMatchTab - Products where no Amazon ASIN was found
 * 
 * Allows users to:
 * - Re-trigger discovery for specific products
 * - Manually search and link ASINs
 * - Mark as "not on Amazon"
 */
const NoMatchTab = () => {
    const [noMatchItems, setNoMatchItems] = useState([]);
    const [products, setProducts] = useState({});
    const [loading, setLoading] = useState(true);
    const [retryLoading, setRetryLoading] = useState(null);

    // Load no-match verifications
    useEffect(() => {
        const noMatchQuery = query(
            collection(db, VERIFICATIONS_PATH),
            where('verificationStatus', '==', 'not_found'),
            orderBy('discoveredAt', 'desc'),
            limit(100)
        );

        const unsubscribe = onSnapshot(
            noMatchQuery,
            (snapshot) => {
                const data = snapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setNoMatchItems(data);
                setLoading(false);
            },
            (error) => {
                console.error('Error fetching no-match items:', error);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, []);

    // Fetch product data
    useEffect(() => {
        const fetchProducts = async () => {
            const skusToFetch = noMatchItems
                .map(v => v.sku)
                .filter(sku => sku && !products[sku]);

            if (skusToFetch.length === 0) return;

            try {
                const productQuery = query(
                    collection(db, PRODUCTS_PATH),
                    where('sku', 'in', skusToFetch.slice(0, 30))
                );

                const snapshot = await getDocs(productQuery);
                const newProducts = { ...products };
                snapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.sku) {
                        newProducts[data.sku] = data;
                    }
                });
                setProducts(newProducts);
            } catch (error) {
                console.error('Error fetching products:', error);
            }
        };

        if (noMatchItems.length > 0) {
            fetchProducts();
        }
    }, [noMatchItems]);

    // Retry discovery for a product
    const handleRetry = useCallback(async (sku) => {
        setRetryLoading(sku);
        try {
            const response = await fetch(`${CLOUD_FUNCTIONS_URL}/asinDiscover`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    skus: [sku],
                    options: { force: true }
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.message || 'Failed to retry');
            }

            const result = await response.json();
            if (result.results?.[0]?.asin) {
                toast.success(`Found match: ${result.results[0].asin}`, { theme: 'colored' });
            } else {
                toast.info('No match found - try again later or search manually');
            }
        } catch (error) {
            console.error('Error retrying:', error);
            toast.error(error.message, { theme: 'colored' });
        } finally {
            setRetryLoading(null);
        }
    }, []);

    // Get barcode display
    const getDisplayBarcode = (verification, product) => {
        const barcodes = verification.barcodesUsed || product?.barcodes || {};
        return barcodes.unit || barcodes.stock || barcodes.other || '-';
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return '-';
        const d = date.toDate ? date.toDate() : new Date(date);
        return d.toLocaleDateString();
    };

    return (
        <div className="p-3">
            <Alert variant="info" className="mb-3">
                <FontAwesomeIcon icon={faTimesCircle} className="me-2" />
                These products were scanned but no Amazon ASIN match was found. You can retry discovery or search manually.
            </Alert>

            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                    <p className="mt-2 text-muted">Loading...</p>
                </div>
            ) : noMatchItems.length === 0 ? (
                <Alert variant="success" className="text-center">
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    All products have matches or haven't been scanned yet.
                </Alert>
            ) : (
                <Table hover responsive className="align-middle">
                    <thead>
                        <tr>
                            <th style={{ width: 50 }}>Image</th>
                            <th>Product</th>
                            <th style={{ width: 120 }}>SKU</th>
                            <th style={{ width: 140 }}>Barcode</th>
                            <th style={{ width: 100 }}>Scanned</th>
                            <th style={{ width: 100 }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {noMatchItems.map(item => {
                            const product = products[item.sku];
                            const isRetrying = retryLoading === item.sku;

                            return (
                                <tr key={item.sku}>
                                    <td>
                                        <img
                                            src={product?.images?.[0] || missingImage}
                                            alt=""
                                            style={{ width: 40, height: 40, objectFit: 'contain' }}
                                            onError={(e) => { e.target.src = missingImage; }}
                                        />
                                    </td>
                                    <td>
                                        <div className="fw-bold small text-truncate" style={{ maxWidth: 250 }}>
                                            {product?.title || item.sku}
                                        </div>
                                        <small className="text-muted">{product?.vendor || '-'}</small>
                                    </td>
                                    <td>
                                        <code className="small">{item.sku}</code>
                                    </td>
                                    <td>
                                        <FontAwesomeIcon icon={faBarcode} className="me-1 text-muted" />
                                        <code className="small">{getDisplayBarcode(item, product)}</code>
                                    </td>
                                    <td>
                                        <small className="text-muted">{formatDate(item.discoveredAt)}</small>
                                    </td>
                                    <td>
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => handleRetry(item.sku)}
                                            disabled={isRetrying}
                                        >
                                            {isRetrying ? (
                                                <Spinner animation="border" size="sm" />
                                            ) : (
                                                <>
                                                    <FontAwesomeIcon icon={faSync} className="me-1" />
                                                    Retry
                                                </>
                                            )}
                                        </Button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            )}
        </div>
    );
};

export default NoMatchTab;
